import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource } from '@angular/material/tree';
import { createActionGroup, emptyProps, props } from '@ngrx/store';
import { FullscreenLayoutAreas, FullscreenLayoutTemplate } from '@src/assets/css/fullscreen.template.scss';
import { ViewStateRecord } from '@src/suite/common/data-models/records/viewstate-record';
import { TreeLikeFlatNode, TreeLikeNode } from '@src/suite/common/data-models/search-results/search-results';
import { SearchType } from '@src/suite/common/enums/enums';
import { NavigationDirection } from '@src/suite/common/enums/navigation-direction';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { FullscreenNewRecordState, SidecarMenu } from '@src/suite/common/store/fullscreen/models/fullscreen.models';

export const OpenFullscreenModalActions = createActionGroup({
    source: 'Fullscreen Open Modal Actions',
    events: {
        // Used only to open the actual dialog
        'Open fullscreen modal': emptyProps(),
        // Set record guid and list guid, and opens the dialog
        'Open Rundown fullscreen dialog': props<OpenRecordProps>(),
        // Set record guid and list guid, and opens the dialog
        'Open topic fullscreen dialog': props<OpenRecordProps>(),
        // Load topic contend and Set topic as record guid and list guid, and opens the dialog
        'Open assignment fullscreen dialog': props<OpenRecordProps & { sidecarMenu: FullscreenLayoutAreas }>(),
        // Set record guid and list guid, and opens the dialog
        'Open Story Fullscreen dialog': props<OpenRecordProps & { listGuid: string; standaloneStory: boolean }>(),

        // Responsible for initializing the empty record, only handled by effect, and not updating the state
        'Init create new record fullscreen': props<{ recordType: ObjectTypes; recordTemplate?: StorytellingRecord; groupData?: any; wireRecord?: StorytellingWireRecord }>(),
        // Used to open the dialog with the new record, and set the new record in the state
        'Open create new record fullscreen': props<FullscreenNewRecordState>(),
        'Close fullscreen modal': emptyProps(),
        'Close fullscreen modal Force': emptyProps(),
        'Close Rundwon fullscreen modal': emptyProps(),
    },
});

export type OpenRecordProps = {
    recordGuid: string;
    recordType: number;
    isMetadata?: boolean;
    fromPinnedItem?: boolean;
    currentNodeUUID?: string;
    topicRecord?: StorytellingTopicRecord;
    treeDataPair?: {
        treeDataSource: MatTreeFlatDataSource<TreeLikeNode<StorytellingRecord>, TreeLikeFlatNode<StorytellingRecord>, string>;
        treeControl: FlatTreeControl<TreeLikeFlatNode<StorytellingRecord>, string>;
    };
};

export const FullscreenActions = createActionGroup({
    source: 'Fullscreen Store',
    events: {
        // Open the item from the list
        'Open home item': emptyProps(),
        // Open topic without reopens the dialog
        'Open Topic': props<OpenRecordProps>(),
        // Open RO without reopens the dialog
        'Open Rundown': props<OpenRecordProps>(),
        // Open assignment without reopens the dialog
        'Open Assignment': props<OpenRecordProps & { sidecarMenu: FullscreenLayoutAreas; vsGuid?: string }>(),
        // Open story without reopens the dialog
        'Open Story': props<OpenRecordProps & { sidecarMenu?: FullscreenLayoutAreas; vsGuid?: string }>(),
        'Navigate Assignment': props<{ direction: NavigationDirection; sidecarMenu: FullscreenLayoutAreas }>(),
        'Navigate Record': props<{ direction: NavigationDirection; throughTopicStories?: boolean }>(),
        /**
         * Navigate Record Directly will navigate to the record, without opening the dialog, it is used from the menu in the navigation,
         * the flag throughViewState is used to determine if we are selecting a record from the current view state, or we are selecting a story from opened topic|rundown
         */
        'Navigate Record Directly': props<{ uniqueNodeId: string; throughTopicStories?: boolean }>(),
        'Set fullscreen template': props<{ template: FullscreenLayoutTemplate }>(),
        'Set Topic current assignment index': props<{ index: number }>(),
        // TODO: This needs to be application level
        'Update current record': props<{ record: StorytellingRecord }>(),
        'Reset current new record': props<{ recordType: ObjectTypes }>(),
        'Update current new record': props<{ record: StorytellingRecord }>(),
        'Add new assignment to new record': props<{ assignment: StorytellingAssignmentRecord }>(),
        'Add new stories to new record': props<{ stories: StorytellingStoryRecord[] }>(),
        'Close current story': emptyProps(),
        'No op Action': emptyProps(),
        'Reset state': emptyProps(),
        'Pin item': props<{ itemGuid: string }>(),
        'Unpin item': props<{ itemGuid: string }>(),
        'Set search term': props<{ searchTerm: string }>(),
        'Set search results guids': props<{ guids: string[] }>(),
        'Remove New Record Items': props<{ record: StorytellingRecord }>(),
        'Set current record has unsaved changes': props<{ hasUnsavedChanges: boolean }>(),
        'Set loading progress bar': props<{ showLoader: boolean }>(),
    },
});

// Sidecar actions
const BASE_SIDECAR_ACTIONS = {
    'Select sidecar menu': props<{ menu: SidecarMenu }>(),
    'Select sidecar assignment': props<{ assignmentGuid: string }>(),
    'Set assignment list guids': props<{ assignmentGuids: Array<string> }>(),
    'Set assignment view states': props<{ viewStates: Array<ViewStateRecord> }>(),
    'Set current assignment view state': props<{ guid: string }>(),
    'Reload assignment based on current view state': emptyProps(),
    'set assignments list search type': props<{ searchType: SearchType }>(),
    'Set topic view states': props<{ viewStates: Array<ViewStateRecord> }>(),
    'Set topic list guids': props<{ topicGuids: Array<string> }>(),
    'Set current topic view state': props<{ guid: string }>(),
    'Topics Search Term': props<{ searchTerm: string }>(),
    'Reload topics based on current view state': emptyProps(),
    'set topics list search type': props<{ searchType: SearchType }>(),
    'Set rundown view states': props<{ viewStates: Array<ViewStateRecord> }>(),
    'Set rundown list guids': props<{ rundownGuids: Array<string> }>(),
    'Set current rundown view state': props<{ guid: string }>(),
    'Rundowns Search Term': props<{ searchTerm: string }>(),
    'Reload rundowns based on current view state': emptyProps(),
    'set rundowns list search type': props<{ searchType: SearchType }>(),
    'Select sidecar story': props<{ guid: string }>(),
    'Set story list guids': props<{ storyGuids: Array<string> }>(),
    'Set story view states': props<{ viewStates: Array<ViewStateRecord> }>(),
    'Set current story view state': props<{ guid: string }>(),
    'Reload story based on current view state': emptyProps(),
    'set stories list search type': props<{ searchType: SearchType }>(),
    'Select sidecar rundown': props<{ guid: string }>(),
    'Select sidecar topic': props<{ guid: string }>(),
    'Set selected story has unsaved changes': props<{ hasUnsavedChanges: boolean }>(),
    'Set current RO selected story': props<{ guid: string }>(),
    'Set current topic selected story': props<{ guid: string }>(),
    'Set current topic selected assignment': props<{ guid: string }>(),
    'Set current Mos item': props<{ mosId: string }>(),
};

// Sidecar effect actions used dispatch the correct sidecar type action
export const SidecarEfffectActions = createActionGroup({
    source: 'Sidecar Store Effect Actions',
    events: {
        'Select sidecar menu': props<{ menu: SidecarMenu; sidecarType: FullscreenLayoutAreas }>(),
        'Load sidecar topic view states': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'Set sidecar topic current view state': props<{ guid: string; sidecarType: FullscreenLayoutAreas }>(),
        'Reload sidecar topics based on current view state': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'set topics list search type': props<{ sidecarType: FullscreenLayoutAreas; searchType: SearchType }>(),
        'Search Topics': props<{ searchTerm: string; sidecarType: FullscreenLayoutAreas }>(),
        'Clear search topics': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'Load sidecar rundown view states': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'Set sidecar rundown current view state': props<{ guid: string; sidecarType: FullscreenLayoutAreas }>(),
        'Reload sidecar rundowns based on current view state': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'set rundowns list search type': props<{ sidecarType: FullscreenLayoutAreas; searchType: SearchType }>(),
        'Search Rundowns': props<{ searchTerm: string; sidecarType: FullscreenLayoutAreas }>(),
        'Clear search rundowns': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'Load sidecar assignment view states': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'Set sidecar assignment current view state': props<{ guid: string; sidecarType: FullscreenLayoutAreas }>(),
        'Reload sidecar assignment based on current view state': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'set assignments list search type': props<{ sidecarType: FullscreenLayoutAreas; searchType: SearchType }>(),
        'Search assignments': props<{ searchTerm: string; sidecarType: FullscreenLayoutAreas }>(),
        'Clear search assignment': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'Clear selected assignment': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'set current topic assignments': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'Load sidecar story view states': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'Set sidecar story current view state': props<{ guid: string; sidecarType: FullscreenLayoutAreas }>(),
        'Reload sidecar story based on current view state': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'set stories list search type': props<{ sidecarType: FullscreenLayoutAreas; searchType: SearchType }>(),
        'Search stories': props<{ searchTerm: string; sidecarType: FullscreenLayoutAreas }>(),
        'Clear search story': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'Clear selected story': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'set current topic stories': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'Open story in sidecar': props<{ guid: string; sidecarType: FullscreenLayoutAreas }>(),
        'Open current rundown': props<{ sidecarMenu: FullscreenLayoutAreas; recordGuid: string }>(),
        'Clear selected rundown': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'Open current topic': props<{ sidecarMenu: FullscreenLayoutAreas; recordGuid: string }>(),
        'Clear selected topic': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'Navigate Story': props<{ direction: NavigationDirection; sidecarMenu: FullscreenLayoutAreas; saveCurrentStory?: boolean }>(),
        'Set selected story has unsaved changes': props<{ sidecarMenu: FullscreenLayoutAreas; hasUnsavedChanges: boolean }>(),
        'set current RO stories': props<{ sidecarType: FullscreenLayoutAreas }>(),
        'Set current RO selected story': props<{ guid: string; sidecarMenu: FullscreenLayoutAreas }>(),
        'Set current topic selected story': props<{ guid: string; sidecarMenu: FullscreenLayoutAreas }>(),
        'Set current topic selected assignment': props<{ guid: string; sidecarMenu: FullscreenLayoutAreas }>(),
        'Navigate Story in Current Topic': props<{ direction: NavigationDirection; sidecarMenu: FullscreenLayoutAreas }>(),
        'Navigate assignment in Current Topic': props<{ direction: NavigationDirection; sidecarMenu: FullscreenLayoutAreas }>(),
        'Navigate Story in Current RO': props<{ direction: NavigationDirection; sidecarMenu: FullscreenLayoutAreas }>(),
        'Set current Mos item': props<{ mosId: string; sidecarMenu: FullscreenLayoutAreas }>(),
        'Open Mos Plugin in sidecar': props<{ mosId: string; sidecarMenu: FullscreenLayoutAreas }>(),
    },
});

// Sidecar actions for each sidecar
// Main sidecar actions
export const MainSidecarActions = createActionGroup({
    source: 'Main Sidecar Store',
    events: {
        ...BASE_SIDECAR_ACTIONS,
    },
});

// Second sidecar actions
export const SecondSidecarActions = createActionGroup({
    source: 'Second Sidecar Store',
    events: {
        ...BASE_SIDECAR_ACTIONS,
    },
});

// Third sidecar actions
export const ThirdSidecarActions = createActionGroup({
    source: 'Third Sidecar Store',
    events: {
        ...BASE_SIDECAR_ACTIONS,
    },
});

// Fourth sidecar actions
export const FourthSidecarActions = createActionGroup({
    source: 'Fourth Sidecar Store',
    events: {
        ...BASE_SIDECAR_ACTIONS,
    },
});
